// Handling Analysis tab (epic #292; Slice 1 = #293 brake balance & lockup).
// Per-RUN tool inside the per-week workspace, so the run is the free variable —
// an inline run selector (default: latest run for this week) drives the fetch.
// DESCRIPTIVE ONLY: cards state what the car did, never "move the bar this way"
// (the Pit-Adjustment engine once shipped reversed — measurement, not advice).
// Absolute slip% is a HELD number (its scale isn't in-sim-validated yet), shown
// muted behind a caption. NASCAR-trio only; other cars show an out-of-domain note.
import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { useRunStore } from "../state/runStore";
import { api, ApiError } from "../utils/api";

interface WheelRow { wheel: string; medianSlipPct: number; maxSlipPct: number; lockedPct: number }
interface PhaseRow { n: number; frontSlipPct: number; rearSlipPct: number }
interface BrakeResult {
  nBraking: number;
  balance: "front locks first" | "rear locks first" | "balanced";
  frontRearDiffPct: number;
  lockupEvents: Record<string, number>;
  absActivePct: number | null;
  staticFrontBiasPct: number | null;
  dynBiasMeanPct: number | null;
  dynBiasRangePct: number | null;
  perPhase: Record<string, PhaseRow> | null;
  held: { frontSlipPct: number; rearSlipPct: number; wheels: WheelRow[] };
  lowSample: boolean;
}
interface ModeStat { mode: string; meanMm: number; minMm: number; maxMm: number; rangeMm: number }
interface PlatformResult {
  modes: ModeStat[];
  rollPerG: number | null;
  pitchPerG: number | null;
  perPhase: Record<string, Record<string, number>> | null;
  nSamples: number;
  lowSample: boolean;
}
interface StaggerResult {
  nStraight: number;
  nCorner: number;
  rearSide: "right" | "left" | "even";
  frontSide: "right" | "left" | "even";
  pathEffect: "grows" | "shrinks" | "flat" | "unknown";
  lowSample: boolean;
  held: {
    rearStaggerMs: number; rearStaggerPct: number | null;
    frontStaggerMs: number; frontStaggerPct: number | null;
    rearCornerSideDiffMs: number | null; rearPathEffectMs: number | null;
    meanSpeedMs: number | null;
  };
}
interface BalanceResult {
  nCorner: number;
  understeerGradDegG: number | null;
  perPhaseGradient: Record<string, number | null> | null;
  steeringRatio: number | null;
  yawAvailable: boolean;
  lowSample: boolean;
  held: { yawRatioMedian: number | null; pushPct: number | null; pushPeakSteerDeg: number | null };
}
interface Handling {
  inDomain: boolean;
  carId: string | null;
  carName: string | null;
  reason?: string;
  unavailable?: string;
  brake?: BrakeResult | null;
  platform?: PlatformResult | null;
  stagger?: StaggerResult | null;
  balance?: BalanceResult | null;
}

const PATH_EFFECT_COPY: Record<string, string> = {
  grows: "Stagger grows through corners (path effect)",
  shrinks: "Stagger drops through corners",
  flat: "Stagger holds steady through corners",
  unknown: "",
};

const MODE_LABEL: Record<string, string> = { heave: "Heave", pitch: "Pitch", roll: "Roll", warp: "Warp" };
const MODE_HINT: Record<string, string> = {
  heave: "average ride height",
  pitch: "front − rear (− = nose-down rake)",
  roll: "left − right (+ = leans right)",
  warp: "diagonal twist (dynamic crossweight)",
};

const PHASE_LABEL: Record<string, string> = { entry: "Entry (braking)", center: "Center", exit: "Exit (throttle)" };
const MIN_PHASE_SAMPLES = 10;  // below this, a phase verdict is a noise fluke — don't assert it

export default function HandlingPanel() {
  const { teamId, trackWeekId: routeWeekId } = useParams<{ teamId?: string; trackWeekId?: string }>();
  const { runs, fetchRuns, selectedTrackWeekId } = useRunStore();
  const weekId = routeWeekId || selectedTrackWeekId || undefined;

  const [runId, setRunId] = useState<string | null>(null);
  const [data, setData] = useState<Handling | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  // Runs for this week (reuse the store; no new endpoint). Default to the latest.
  useEffect(() => {
    if (teamId && weekId) void fetchRuns(teamId, weekId);
  }, [teamId, weekId, fetchRuns]);

  const weekRuns = useMemo(
    () => runs.filter((r) => r.trackWeekId === weekId),
    [runs, weekId],
  );

  useEffect(() => {
    if (weekRuns.length && (!runId || !weekRuns.some((r) => r.id === runId))) {
      setRunId(weekRuns[0].id); // runs come newest-first
    }
  }, [weekRuns, runId]);

  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api<Handling>(`/api/strategy/handling?run_id=${runId}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => {
        if (cancelled) return;
        // 503 = analyzer semaphore busy (re-read guard) — "try again", not permanent.
        setError(
          err instanceof ApiError && err.status === 503
            ? "The analyzer is busy right now — try again in a few seconds."
            : "Couldn't analyze this run."
        );
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [runId, reloadNonce]);

  return (
    <div className="page fade-in">
      <header className="editorial-header">
        <div className="editorial-meta">
          <span className="eyebrow">Telemetry</span>
        </div>
        <h1 className="editorial-title">Handling Analysis</h1>
      </header>

      {/* Run selector */}
      <div style={st.selectorRow}>
        <label style={st.selectorLabel} htmlFor="handling-run">Run</label>
        <select
          id="handling-run"
          value={runId || ""}
          onChange={(e) => setRunId(e.target.value || null)}
          style={st.select}
          disabled={!weekRuns.length}
        >
          {!weekRuns.length && <option value="">No runs this week</option>}
          {weekRuns.map((r) => (
            <option key={r.id} value={r.id}>
              {(r.displayName || "Driver")} · {r.setupName || "setup"}
              {r.totalLaps != null ? ` · ${r.totalLaps} laps` : ""}
              {r.sessionDate ? ` · ${new Date(r.sessionDate).toLocaleDateString()}` : ""}
            </option>
          ))}
        </select>
      </div>

      {loading && <div className="empty-state" style={{ minHeight: "10rem" }}><div className="empty-state-text">Analyzing run…</div></div>}
      {error && !loading && (
        <div className="empty-state" style={{ minHeight: "10rem" }}>
          <div className="empty-state-text">{error}</div>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ marginTop: "0.75rem" }}
            onClick={() => setReloadNonce((n) => n + 1)}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && data && !data.inDomain && (
        <div className="card" style={st.card}>
          <div style={st.cardTitle}>Not calibrated for this car</div>
          <div style={st.muted}>{data.reason || "Handling analysis isn't calibrated for this car."}{data.carName ? ` (${data.carName})` : ""}</div>
        </div>
      )}

      {!loading && !error && data && data.inDomain && !data.brake && !data.platform && !data.stagger && !data.balance && (
        <div className="card" style={st.card}>
          <div style={st.cardTitle}>Handling</div>
          <div style={st.muted}>{data.unavailable || "This run's telemetry doesn't have the channels needed for handling analysis."}</div>
        </div>
      )}

      {!loading && !error && data?.brake && <BrakeCard b={data.brake} carName={data.carName} />}
      {!loading && !error && data?.platform && <PlatformCard p={data.platform} carName={data.carName} />}
      {!loading && !error && data?.stagger && <StaggerCard s={data.stagger} carName={data.carName} />}
      {!loading && !error && data?.balance && <BalanceCard b={data.balance} carName={data.carName} />}
    </div>
  );
}

function BalanceCard({ b, carName }: { b: BalanceResult; carName: string | null }) {
  const grad = (v: number | null) => (v == null || !Number.isFinite(v) ? "—" : `${v.toFixed(1)}°/g`);
  return (
    <div className="card" style={{ ...st.card, marginTop: "1.25rem" }}>
      <div style={st.cardHead}>
        <div style={st.cardTitle}>Balance</div>
        {carName && <span style={st.carTag}>{carName}</span>}
      </div>
      <div style={st.headline}>Understeer gradient {grad(b.understeerGradDegG)}</div>
      <div style={st.caption}>
        Steering degrees per g of lateral load — higher means more front push. Measured, not setup
        advice.{b.lowSample ? " Few cornering samples; read with caution." : ""}
      </div>

      {b.perPhaseGradient && (
        <>
          <div style={st.sectionLabel}>Gradient by corner phase</div>
          <table className="table" style={{ marginBottom: "1rem" }}>
            <thead><tr><th>Phase</th><th style={st.num}>Gradient (°/g)</th></tr></thead>
            <tbody>
              {["entry", "center", "exit"].map((ph) => (
                <tr key={ph}>
                  <td>{PHASE_LABEL[ph] || ph}</td>
                  <td style={st.num}>{grad(b.perPhaseGradient?.[ph] ?? null)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* HELD — yaw ratio + push detector pending in-sim validation. */}
      <details style={st.details}>
        <summary style={st.summary}>Yaw &amp; push</summary>
        <div style={st.caption}>
          Not validated against an in-sim reference run yet — read the gradient above.
        </div>
        <div style={st.statGrid}>
          <Stat
            label="Neutral-steer yaw ratio"
            value={b.yawAvailable ? (b.held.yawRatioMedian == null ? "—" : b.held.yawRatioMedian.toFixed(2)) : "rack-steer — n/a"}
            muted
          />
          <Stat label="Push onset" value={b.held.pushPeakSteerDeg == null ? "—" : `${b.held.pushPeakSteerDeg.toFixed(0)}°`} muted />
          <Stat label="Time past push onset" value={b.held.pushPct == null ? "—" : `${b.held.pushPct.toFixed(0)}%`} muted />
        </div>
      </details>
    </div>
  );
}

function StaggerCard({ s, carName }: { s: StaggerResult; carName: string | null }) {
  const headline =
    s.rearSide === "right" ? "Right-side stagger"
    : s.rearSide === "left" ? "Left-side stagger"
    : "Minimal stagger";
  // No path copy when there's no clear side (keeps the two strings from contradicting).
  const pathCopy = s.rearSide === "even" ? "" : (PATH_EFFECT_COPY[s.pathEffect] || "");
  const cap = (v: string) => v.charAt(0).toUpperCase() + v.slice(1);
  const ms = (v: number | null) => (v == null || !Number.isFinite(v) ? "—" : v.toFixed(2));
  const pct = (v: number | null) => (v == null || !Number.isFinite(v) ? "—" : `${v.toFixed(1)}%`);

  return (
    <div className="card" style={{ ...st.card, marginTop: "1.25rem" }}>
      <div style={st.cardHead}>
        <div style={st.cardTitle}>Dynamic stagger</div>
        {carName && <span style={st.carTag}>{carName}</span>}
      </div>
      <div style={st.headline}>{headline}</div>
      <div style={st.caption}>
        Measured wheel-speed side difference — not setup advice.{s.lowSample ? " Few straight-line samples; read with caution." : ""}
        {pathCopy ? ` ${pathCopy}.` : ""}
      </div>

      <div style={st.statGrid}>
        <Stat label="Rear (right − left)" value={cap(s.rearSide)} />
        <Stat label="Front (right − left)" value={cap(s.frontSide)} />
        <Stat label="Straight samples" value={String(s.nStraight)} />
        <Stat label="Corner samples" value={String(s.nCorner)} />
      </div>

      {/* HELD — absolute m/s depends on the unvalidated wheel-speed channel units. */}
      <details style={st.details}>
        <summary style={st.summary}>Raw stagger values</summary>
        <div style={st.caption}>
          Absolute side-difference depends on the iRacing wheel-speed channel units, which aren't
          validated against an in-sim reference run yet — read the direction above, not these numbers.
        </div>
        <div style={st.statGrid}>
          <Stat label="Rear stagger (straight)" value={`${ms(s.held.rearStaggerMs)} m/s · ${pct(s.held.rearStaggerPct)}`} muted />
          <Stat label="Front stagger (straight)" value={`${ms(s.held.frontStaggerMs)} m/s · ${pct(s.held.frontStaggerPct)}`} muted />
          <Stat label="Rear side-diff (corner)" value={`${ms(s.held.rearCornerSideDiffMs)} m/s`} muted />
          <Stat label="Path effect (corner − straight)" value={`${ms(s.held.rearPathEffectMs)} m/s`} muted />
          <Stat label="Mean straight speed" value={`${ms(s.held.meanSpeedMs)} m/s`} muted />
        </div>
      </details>
    </div>
  );
}

function PlatformCard({ p, carName }: { p: PlatformResult; carName: string | null }) {
  const byMode = Object.fromEntries(p.modes.map((m) => [m.mode, m]));
  const pitch = byMode.pitch?.meanMm ?? 0;
  // Sign-driven wording (scale-robust); the measured mm leads, never a fixed mm band.
  const headline =
    pitch < 0 ? `Runs nose-down · pitch ${pitch.toFixed(0)} mm`
    : pitch > 0 ? `Runs nose-up · pitch ${pitch.toFixed(0)} mm`
    : "Flat platform";
  const mm = (v: number) => (Number.isFinite(v) ? `${v.toFixed(0)} mm` : "—");
  const perG = (v: number | null) => (v == null ? "—" : `${v.toFixed(1)} mm/g`);

  return (
    <div className="card" style={{ ...st.card, marginTop: "1.25rem" }}>
      <div style={st.cardHead}>
        <div style={st.cardTitle}>Ride-height platform</div>
        {carName && <span style={st.carTag}>{carName}</span>}
      </div>
      <div style={st.headline}>{headline}</div>
      <div style={st.caption}>Measured ride-height attitude across the run — not setup advice.{p.lowSample ? " Few racing samples; read with caution." : ""}</div>

      <div style={st.statGrid}>
        {["heave", "pitch", "roll", "warp"].map((m) => {
          const s = byMode[m];
          if (!s) return null;
          return (
            <div key={m} style={st.stat}>
              <div style={st.statLabel}>{MODE_LABEL[m]} <span style={{ color: "var(--text-muted)" }}>· {MODE_HINT[m]}</span></div>
              <div style={st.statValue}>{mm(s.meanMm)} <span style={{ fontSize: "0.85rem", fontWeight: 500, color: "var(--text-secondary)" }}>range {mm(s.rangeMm)}</span></div>
            </div>
          );
        })}
      </div>

      <div style={st.statGrid}>
        <Stat label="Roll per g" value={perG(p.rollPerG)} />
        <Stat label="Pitch per g" value={perG(p.pitchPerG)} />
      </div>

      {p.perPhase && (
        <details style={st.details}>
          <summary style={st.summary}>By corner phase</summary>
          <table className="table" style={{ marginTop: "0.5rem" }}>
            <thead><tr><th>Phase</th><th style={st.num}>Samples</th><th style={st.num}>Heave</th><th style={st.num}>Pitch</th><th style={st.num}>Roll</th><th style={st.num}>Warp</th></tr></thead>
            <tbody>
              {Object.entries(p.perPhase).map(([ph, row]) => {
                const thin = row.n < MIN_PHASE_SAMPLES;  // don't print a flat "0 mm" off ~no samples
                const cell = (v: number) => (thin ? "—" : (v ?? 0).toFixed(0));
                return (
                  <tr key={ph} style={thin ? { color: "var(--text-muted)" } : undefined}>
                    <td>{PHASE_LABEL[ph] || ph}</td>
                    <td style={st.num}>{row.n}</td>
                    <td style={st.num}>{cell(row.heave)}</td>
                    <td style={st.num}>{cell(row.pitch)}</td>
                    <td style={st.num}>{cell(row.roll)}</td>
                    <td style={st.num}>{cell(row.warp)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}

function BrakeCard({ b, carName }: { b: BrakeResult; carName: string | null }) {
  const headline =
    b.balance === "balanced" ? "Front and rear lock together"
    : b.balance === "front locks first" ? "Fronts lock before rears under braking"
    : "Rears lock before fronts under braking";

  const pct = (v: number | null, d = 0) => (v == null ? "—" : `${v.toFixed(d)}%`);

  return (
    <div className="card" style={st.card}>
      <div style={st.cardHead}>
        <div style={st.cardTitle}>Brake balance &amp; lockup</div>
        {carName && <span style={st.carTag}>{carName}</span>}
      </div>

      <div style={st.headline}>{headline}</div>
      <div style={st.caption}>Measurement from this run — not setup advice.{b.lowSample ? " Few braking samples; read with caution." : ""}</div>

      <div style={st.statGrid}>
        <Stat label="Braking samples" value={String(b.nBraking)} />
        <Stat label="ABS active" value={pct(b.absActivePct)} />
        <Stat label="Static front bias" value={pct(b.staticFrontBiasPct, 1)} />
        <Stat label="Dynamic bias" value={b.dynBiasMeanPct == null ? "—" : `${b.dynBiasMeanPct.toFixed(1)}% ±${(b.dynBiasRangePct ?? 0).toFixed(1)}`} />
      </div>
      <div style={st.caption}>Bias is read from the setup/telemetry as percent.</div>

      {/* Per-phase balance — front-vs-rear (symmetric, scale-invariant), gated on samples */}
      {b.perPhase && (
        <>
          <div style={st.sectionLabel}>Locks more, by corner phase</div>
          <table className="table" style={{ marginBottom: "1rem" }}>
            <thead><tr><th>Phase</th><th style={st.num}>Samples</th><th>Result</th></tr></thead>
            <tbody>
              {Object.entries(b.perPhase).map(([ph, row]) => {
                const more = row.n < MIN_PHASE_SAMPLES ? "too few to call"
                  : Math.abs(row.frontSlipPct - row.rearSlipPct) < 1 ? "even"
                  : row.frontSlipPct > row.rearSlipPct ? "front locks more" : "rear locks more";
                return (
                  <tr key={ph}>
                    <td>{PHASE_LABEL[ph] || ph}</td>
                    <td style={st.num}>{row.n}</td>
                    <td style={{ color: row.n < MIN_PHASE_SAMPLES ? "var(--text-muted)" : undefined }}>{more}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* HELD — these depend on the in-sim-unvalidated absolute slip scale + the 15%
          lockup threshold, so they sit behind the disclosure, not in the trusted body. */}
      <details style={st.details}>
        <summary style={st.summary}>Raw slip detail</summary>
        <div style={st.caption}>
          These depend on an absolute slip scale and a 15% lockup threshold that aren't validated
          against an in-sim reference run yet — read the front/rear balance above, not these numbers.
        </div>
        <div style={st.statGrid}>
          <Stat label="Front slip (mean)" value={`${b.held.frontSlipPct.toFixed(1)}%`} muted />
          <Stat label="Rear slip (mean)" value={`${b.held.rearSlipPct.toFixed(1)}%`} muted />
        </div>
        <table className="table" style={{ marginTop: "0.5rem" }}>
          <thead><tr><th>Wheel</th><th style={st.num}>Locked</th><th style={st.num}>Events</th></tr></thead>
          <tbody>
            {b.held.wheels.map((w) => (
              <tr key={w.wheel}>
                <td style={st.muted}>{w.wheel}</td>
                <td style={{ ...st.num, color: "var(--text-muted)" }}>{w.lockedPct.toFixed(0)}%</td>
                <td style={{ ...st.num, color: "var(--text-muted)" }}>{b.lockupEvents[w.wheel] ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

function Stat({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div style={st.stat}>
      <div style={st.statLabel}>{label}</div>
      <div style={{ ...st.statValue, color: muted ? "var(--text-muted)" : "var(--text-strong)" }}>{value}</div>
    </div>
  );
}

const st: Record<string, CSSProperties> = {
  selectorRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: "1.25rem" },
  selectorLabel: { fontFamily: "var(--font-body)", fontSize: 13, color: "var(--text-secondary)", fontWeight: 600 },
  select: {
    background: "var(--ink-2)", color: "var(--text-default)", border: "1px solid var(--rule-strong)",
    borderRadius: "var(--r-md)", padding: "0.5rem 0.7rem", fontFamily: "var(--font-body)", fontSize: 13, minWidth: 280,
  },
  card: { padding: "1.25rem 1.5rem", maxWidth: 640 },
  cardHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
  cardTitle: { fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1rem", color: "var(--text-strong)" },
  carTag: { fontFamily: "var(--font-body)", fontSize: 12, color: "var(--text-secondary)", border: "1px solid var(--rule)", borderRadius: 999, padding: "0.1rem 0.6rem" },
  headline: { fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1.35rem", color: "var(--text-strong)", marginTop: "0.85rem" },
  caption: { fontFamily: "var(--font-body)", fontSize: 13, color: "var(--text-secondary)", marginTop: 4, lineHeight: 1.45, maxWidth: 460 },
  statGrid: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "0.75rem 1.5rem", margin: "1.1rem 0" },
  stat: { borderTop: "1px solid var(--rule)", paddingTop: "0.5rem" },
  statLabel: { fontFamily: "var(--font-body)", fontSize: 12, color: "var(--text-secondary)" },
  statValue: { fontFamily: "var(--font-body)", fontSize: "1.1rem", fontWeight: 700, fontVariantNumeric: "tabular-nums", marginTop: 2 },
  sectionLabel: { fontFamily: "var(--font-body)", fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", margin: "0.4rem 0 0.5rem" },
  num: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
  muted: { fontFamily: "var(--font-body)", fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5 },
  details: { marginTop: "0.5rem", borderTop: "1px solid var(--rule)", paddingTop: "0.75rem" },
  summary: { fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", cursor: "pointer" },
};
